import {
  useCallback,
  type Dispatch,
  type MutableRefObject,
  type RefObject,
  type SetStateAction,
} from 'react';
import { flushSync } from 'react-dom';
import type { MessengerMessage } from '@/api/client';
import { directThreadLoadMessageContext } from '@/components/Messenger/cache/directThreadCacheStore';
import { dmMsgQuerySelector } from '@/components/Messenger/shared/viewport/messengerDmScrollAnchor';

const HIGHLIGHT_CLASS = 'dmBubble__highlighted';
const HIGHLIGHT_MS = 1500;
const LOADING_POLL_MS = 25;
const LOADING_POLL_MAX_TICKS = 160;
const CONTEXT_LOAD_MAX_ATTEMPTS = 3;
const HIGHLIGHT_MAX_ATTEMPTS = 8;
const HIGHLIGHT_RETRY_MS = 50;
const SETTLE_RAF_MAX = 8;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export function centerMessageSlotInViewport(viewport: HTMLElement, target: Element): void {
  const vr = viewport.getBoundingClientRect();
  const tr = target.getBoundingClientRect();
  const targetTopInViewport = tr.top - vr.top;
  const targetCenterOffset = targetTopInViewport + tr.height / 2 - vr.height / 2;
  viewport.scrollTop += targetCenterOffset;
}

interface UseMessageJumpNavigationParams {
  threadId: string;
  viewportRef: RefObject<HTMLElement | null>;
  loadingRef: MutableRefObject<boolean>;
  messagesRef: MutableRefObject<MessengerMessage[]>;
  setJumpScrollIntentSignal: Dispatch<SetStateAction<number>>;
  onJumpSettled?: (nonce: number) => void;
  setSettledJumpNonce?: Dispatch<SetStateAction<number | null>>;
  lastHandledJumpNonceRef?: MutableRefObject<number | null>;
}

interface ExecuteMessageJumpOptions {
  messageId: string;
  cancelled?: () => boolean;
  nonce?: number;
  withSettle?: boolean;
}

async function waitThreadLoadingDone(
  loadingRef: MutableRefObject<boolean>,
  cancelled?: () => boolean
): Promise<boolean> {
  let ticks = 0;
  while (!cancelled?.() && loadingRef.current && ticks < LOADING_POLL_MAX_TICKS) {
    await sleep(LOADING_POLL_MS);
    ticks += 1;
  }
  return !loadingRef.current;
}

async function loadMessageContextWithRetry(
  threadId: string,
  messageId: string,
  messagesRef: MutableRefObject<MessengerMessage[]>,
  loadingRef: MutableRefObject<boolean>,
  cancelled?: () => boolean
): Promise<boolean> {
  if (!(await waitThreadLoadingDone(loadingRef, cancelled))) return false;

  let contextAttempts = 0;
  while (
    !cancelled?.() &&
    !messagesRef.current.some((m) => m.id === messageId) &&
    contextAttempts < CONTEXT_LOAD_MAX_ATTEMPTS
  ) {
    contextAttempts += 1;
    await directThreadLoadMessageContext(threadId, messageId);
    if (!(await waitThreadLoadingDone(loadingRef, cancelled))) return false;
  }

  return !cancelled?.();
}

function tryHighlightMessage(
  viewport: HTMLElement,
  messageId: string,
  setJumpScrollIntentSignal: Dispatch<SetStateAction<number>>,
  withSettle: boolean,
  nonce: number | undefined,
  params: Pick<
    UseMessageJumpNavigationParams,
    'onJumpSettled' | 'setSettledJumpNonce' | 'lastHandledJumpNonceRef'
  >
): boolean {
  const el = dmMsgQuerySelector(viewport, messageId);
  if (!el) return false;

  flushSync(() => {
    setJumpScrollIntentSignal((value) => value + 1);
  });
  centerMessageSlotInViewport(viewport, el);
  el.classList.add(HIGHLIGHT_CLASS);
  window.setTimeout(() => el.classList.remove(HIGHLIGHT_CLASS), HIGHLIGHT_MS);

  if (nonce != null && params.lastHandledJumpNonceRef) {
    params.lastHandledJumpNonceRef.current = nonce;
  }

  if (!withSettle || nonce == null) return true;

  let settleCount = 0;
  const settleCenter = () => {
    if (!viewport.isConnected || !el.isConnected) {
      params.setSettledJumpNonce?.(nonce);
      params.onJumpSettled?.(nonce);
      return;
    }
    centerMessageSlotInViewport(viewport, el);
    settleCount += 1;
    if (settleCount < SETTLE_RAF_MAX) {
      requestAnimationFrame(settleCenter);
    } else {
      params.setSettledJumpNonce?.(nonce);
      params.onJumpSettled?.(nonce);
    }
  };
  requestAnimationFrame(settleCenter);
  return true;
}

async function executeMessageJump(
  params: UseMessageJumpNavigationParams,
  options: ExecuteMessageJumpOptions
): Promise<boolean> {
  const { messageId, cancelled, nonce, withSettle = false } = options;
  const { threadId, viewportRef, loadingRef, messagesRef, setJumpScrollIntentSignal } = params;

  const loaded = await loadMessageContextWithRetry(
    threadId,
    messageId,
    messagesRef,
    loadingRef,
    cancelled
  );
  if (!loaded || cancelled?.()) return false;

  return new Promise((resolve) => {
    let attempts = 0;

    const tryHighlight = () => {
      if (cancelled?.()) {
        resolve(false);
        return;
      }

      const viewport = viewportRef.current;
      if (!viewport) {
        resolve(false);
        return;
      }

      if (
        tryHighlightMessage(viewport, messageId, setJumpScrollIntentSignal, withSettle, nonce, params)
      ) {
        resolve(true);
        return;
      }

      attempts += 1;
      if (attempts >= HIGHLIGHT_MAX_ATTEMPTS) {
        if (nonce != null) {
          if (params.lastHandledJumpNonceRef) {
            params.lastHandledJumpNonceRef.current = nonce;
          }
          params.setSettledJumpNonce?.(nonce);
          params.onJumpSettled?.(nonce);
        }
        resolve(false);
        return;
      }

      window.setTimeout(() => requestAnimationFrame(tryHighlight), HIGHLIGHT_RETRY_MS);
    };

    requestAnimationFrame(() => requestAnimationFrame(tryHighlight));
  });
}

export interface UseMessageJumpNavigationResult {
  jumpToMessageById: (messageId: string) => void;
  runJumpToMessageIntent: (params: {
    messageId: string;
    nonce: number;
    cancelled: () => boolean;
  }) => Promise<void>;
}

export function useMessageJumpNavigation(
  params: UseMessageJumpNavigationParams
): UseMessageJumpNavigationResult {
  const {
    threadId,
    viewportRef,
    loadingRef,
    messagesRef,
    setJumpScrollIntentSignal,
    onJumpSettled,
    setSettledJumpNonce,
    lastHandledJumpNonceRef,
  } = params;

  const jumpToMessageById = useCallback(
    (messageId: string) => {
      flushSync(() => {
        setJumpScrollIntentSignal((value) => value + 1);
      });
      void executeMessageJump(
        {
          threadId,
          viewportRef,
          loadingRef,
          messagesRef,
          setJumpScrollIntentSignal,
          onJumpSettled,
          setSettledJumpNonce,
          lastHandledJumpNonceRef,
        },
        { messageId }
      );
    },
    [
      threadId,
      viewportRef,
      loadingRef,
      messagesRef,
      setJumpScrollIntentSignal,
      onJumpSettled,
      setSettledJumpNonce,
      lastHandledJumpNonceRef,
    ]
  );

  const runJumpToMessageIntent = useCallback(
    async ({
      messageId,
      nonce,
      cancelled,
    }: {
      messageId: string;
      nonce: number;
      cancelled: () => boolean;
    }) => {
      try {
        await executeMessageJump(
          {
            threadId,
            viewportRef,
            loadingRef,
            messagesRef,
            setJumpScrollIntentSignal,
            onJumpSettled,
            setSettledJumpNonce,
            lastHandledJumpNonceRef,
          },
          { messageId, nonce, cancelled, withSettle: true }
        );
      } catch {
        if (cancelled()) return;
        if (lastHandledJumpNonceRef) {
          lastHandledJumpNonceRef.current = nonce;
        }
        setSettledJumpNonce?.(nonce);
        onJumpSettled?.(nonce);
      }
    },
    [
      threadId,
      viewportRef,
      loadingRef,
      messagesRef,
      setJumpScrollIntentSignal,
      onJumpSettled,
      setSettledJumpNonce,
      lastHandledJumpNonceRef,
    ]
  );

  return { jumpToMessageById, runJumpToMessageIntent };
}
